import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Clock } from 'lucide-react';
import { useSaisieLigneMutation } from '../../services/notesDevoirsApi';
import HistoriqueSaisieModal from './HistoriqueSaisieModal';

interface SaisieLigneProps {
  matiereId: number;
  matiereLabel?: string;
  anneeAcademique: string;
  semestre: 'S1' | 'S2';
  classe: string;
  onSuccess?: () => void;
}

const SaisieLigne: React.FC<SaisieLigneProps> = ({
  matiereId,
  matiereLabel,
  anneeAcademique,
  semestre,
  classe,
  onSuccess,
}) => {
  const [saisieLigne, { isLoading: isSaving }] = useSaisieLigneMutation();

  const [matricule, setMatricule] = useState('');
  // Fix 2026-08-27 : plus de cases absent — si le champ est vide au blur, note = 0.
  const [note1, setNote1] = useState('');
  const [note2, setNote2] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showHistorique, setShowHistorique] = useState(false);

  const note1Ref = useRef<HTMLInputElement>(null);
  const note2Ref = useRef<HTMLInputElement>(null);

  // Avertissement avant fermeture si saisie en cours non validée
  useEffect(() => {
    const hasUnsaved = !!matricule.trim() && (!!note1 || !!note2);
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsaved) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [matricule, note1, note2]);
  const [dernieresNotes, setDernieresNotes] = useState<
    Array<{
      matricule: string;
      note1: string;
      note2: string;
      moyenne: string;
      timestamp: Date;
    }>
  >([]);

  // Calculer la moyenne en temps réel (vide = 0)
  const calculerMoyenne = (): number | null => {
    const n1 = note1.trim() === '' ? 0 : parseFloat(note1);
    const n2 = note2.trim() === '' ? 0 : parseFloat(note2);
    if (!isNaN(n1) && !isNaN(n2)) return (n1 + n2) / 2;
    return null;
  };

  const moyenne = calculerMoyenne();

  // Soumet la fiche en utilisant des valeurs explicites (pour le raccourci C/Espace
  // qui contourne le state async). Vide → 0.
  const submitWith = useCallback(async (n1: string, n2: string) => {
    setMessage(null);
    if (!matricule.trim()) {
      setMessage({ type: 'error', text: 'Veuillez entrer un matricule' });
      return;
    }
    try {
      const n1Val = n1.trim() === '' ? 0 : parseFloat(n1);
      const n2Val = n2.trim() === '' ? 0 : parseFloat(n2);
      const result = await saisieLigne({
        matricule: matricule.trim(),
        matiere_id: matiereId,
        annee_academique: anneeAcademique,
        semestre: semestre,
        note1: n1Val,
        absent1: false,
        note2: n2Val,
        absent2: false,
      }).unwrap();
      if (result.success) {
        setMessage({
          type: 'success',
          text: `Note ${result.created ? 'créée' : 'modifiée'} ! Moyenne: ${result.moyenne_devoirs}`,
        });
        setDernieresNotes(prev => [
          {
            matricule, note1: String(n1Val), note2: String(n2Val),
            moyenne: result.moyenne_devoirs, timestamp: new Date(),
          },
          ...prev.slice(0, 9),
        ]);
        setMatricule('');
        setNote1('');
        setNote2('');
        setTimeout(() => document.getElementById('matricule-input')?.focus(), 100);
        onSuccess?.();
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de la sauvegarde' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.data?.error || 'Erreur lors de la sauvegarde' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matricule, matiereId, anneeAcademique, semestre, saisieLigne, onSuccess]);

  // Raccourci clavier : C ou Espace dans D1/D2 → copie D1→D2 + valide + suivant
  const handleNoteKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'c' || e.key === 'C' || e.key === ' ') {
      if (!note1.trim()) return;
      e.preventDefault();
      setNote2(note1);
      submitWith(note1, note1);
    }
  };

  // Gérer la soumission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    submitWith(note1, note2);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Formulaire de saisie */}
      <div className="lg:col-span-2">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {/* Header condensé (fix 2026-08-27) */}
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap text-sm text-gray-700 dark:text-gray-300">
              <span className="font-semibold text-gray-900 dark:text-gray-100">Saisie ligne par ligne</span>
              <span className="text-gray-400">·</span>
              <span>{classe}</span>
              <span className="text-gray-400">·</span>
              <span>{semestre}</span>
            </div>
            <button
              onClick={() => setShowHistorique(true)}
              className="inline-flex items-center gap-1 px-3 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              title="Historique des modifications pour cette matière/classe/semestre"
            >
              <Clock size={12} /> Historique
            </button>
          </div>

          {message && (
            <div
              className={`mx-6 mt-4 px-4 py-3 rounded ${
                message.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800'
              }`}
            >
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Matricule */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Matricule de l'étudiant <span className="text-red-500">*</span>
              </label>
              <input
                id="matricule-input"
                type="text"
                value={matricule}
                onChange={(e) => setMatricule(e.target.value)}
                required
                autoFocus
                className="w-full px-3 py-2 text-lg border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: 2024001"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Tapez le matricule et appuyez sur Tab pour passer aux notes
              </p>
            </div>

            {/* Notes D.1 et D.2 (fix 2026-08-27 : plus de case absent, vide = 0) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Devoir 1 (D.1) <span className="text-xs text-gray-500 font-normal">— vide = 0</span>
                </h4>
                <input
                  ref={note1Ref}
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={note1}
                  onChange={(e) => setNote1(e.target.value)}
                  onBlur={(e) => { if (e.target.value.trim() === '') setNote1('0'); }}
                  onKeyDown={handleNoteKeyDown}
                  className="w-full px-3 py-2 text-lg border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Note"
                />
              </div>

              <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Devoir 2 (D.2) <span className="text-xs text-gray-500 font-normal">— vide = 0</span>
                </h4>
                <input
                  ref={note2Ref}
                  type="number"
                  step="0.01"
                  min="0"
                  max="20"
                  value={note2}
                  onChange={(e) => setNote2(e.target.value)}
                  onBlur={(e) => { if (e.target.value.trim() === '') setNote2('0'); }}
                  onKeyDown={handleNoteKeyDown}
                  className="w-full px-3 py-2 text-lg border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Note"
                />
              </div>
            </div>

            {/* Moyenne calculée */}
            {moyenne !== null && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-900 dark:text-blue-300">Moyenne :</span>
                  <span className="text-2xl font-bold text-blue-900 dark:text-blue-200">{moyenne.toFixed(2)} / 20</span>
                </div>
              </div>
            )}

            {/* Boutons */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => {
                  setMatricule('');
                  setNote1('');
                  setNote2('');
                  setMessage(null);
                  document.getElementById('matricule-input')?.focus();
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Effacer
              </button>
              <button
                type="submit"
                disabled={isSaving || !matricule.trim()}
                className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {isSaving ? 'Enregistrement...' : 'Valider et Passer au suivant'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Historique des dernières saisies */}
      <div className="lg:col-span-1">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">Dernières saisies</h3>
          </div>
          <div className="p-4">
            {dernieresNotes.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                Aucune note saisie pour le moment
              </p>
            ) : (
              <div className="space-y-2">
                {dernieresNotes.map((note, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900 dark:text-gray-100">{note.matricule}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        D.1: {note.note1} | D.2: {note.note2}
                      </p>
                    </div>
                    <div className="ml-3 text-right">
                      <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{note.moyenne}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {note.timestamp.toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Raccourcis clavier */}
        <div className="mt-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700 p-4">
          <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">Raccourcis clavier</h4>
          <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
            <li>• <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 rounded font-bold">C</kbd> ou <kbd className="px-1 py-0.5 bg-amber-200 dark:bg-amber-700 text-amber-900 rounded font-bold">Espace</kbd> dans D1/D2 : copie D1→D2 + valide</li>
            <li>• Tab : passer au champ suivant</li>
            <li>• Entrée : valider et passer au suivant</li>
          </ul>
        </div>
      </div>

      {/* Modale historique (fix 2026-08-27) */}
      <HistoriqueSaisieModal
        open={showHistorique}
        onClose={() => setShowHistorique(false)}
        matiereId={matiereId}
        matiereLabel={matiereLabel}
        classe={classe}
        semestre={semestre}
        anneeAcademique={anneeAcademique}
      />
    </div>
  );
};

export default SaisieLigne;
